import React from "react";
import { useTranslation } from "react-i18next";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { Sparkles, Heart, Globe, ShieldCheck, Check, ArrowRight } from "lucide-react";

export default function AboutPage() {
    const { t } = useTranslation();
    const VALUES = [
        { icon: Sparkles,    title: t("about.aiTitle"),    text: t("about.aiText") },
        { icon: Heart,       title: t("about.humanTitle"), text: t("about.humanText") },
        { icon: Globe,       title: t("about.multiTitle"), text: t("about.multiText") },
        { icon: ShieldCheck, title: t("about.gdprTitle"),  text: t("about.gdprText") },
    ];
    return (
        <PublicLayout>
            <Seo
                title="Über uns: 20+ Jahre Personal- & Integrationsexpertise | Bewerbung24.ai"
                description="Bewerbung24.ai verbindet moderne KI mit über 20 Jahren Erfahrung in Recruiting, Karriereberatung, Outplacement und Fachkräfteintegration — aus der Praxis für die Praxis."
                path="/ueber-uns"
            />
            <section className="max-w-5xl mx-auto px-6 lg:px-8 pt-20 lg:pt-28 pb-12">
                <div className="h-1.5 w-16 rounded-full mb-7" style={{ background: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)" }} />
                <span className="eyebrow-warm">{t("about.badge")}</span>
                <h1 className="font-heading mt-5 text-5xl sm:text-6xl lg:text-[5rem] font-bold tracking-tighter text-warm-strong leading-[0.95]">
                    {t("about.h1Part1")}<br />
                    <span className="gradient-warm">{t("about.h1Highlight")}</span>
                </h1>
                <p className="mt-7 text-lg lg:text-xl text-warm max-w-2xl leading-relaxed">{t("about.subtitle")}</p>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
                <div className="grid md:grid-cols-2 gap-5">
                    {VALUES.map((v, i) => (
                        <div key={v.title} className="group p-8 rounded-3xl bg-white hover:shadow-xl hover:-translate-y-1 transition-all" style={{ borderLeft: `5px solid ${i % 2 === 0 ? "#E7343F" : "#F9B000"}` }}>
                            <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white transition-transform group-hover:scale-110" style={{ background: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)" }}>
                                <v.icon size={20} strokeWidth={1.5} />
                            </div>
                            <h3 className="font-heading mt-6 text-2xl font-bold text-warm-strong">{v.title}</h3>
                            <p className="mt-3 text-warm leading-relaxed">{v.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="max-w-5xl mx-auto px-6 lg:px-8 pb-24" data-testid="about-expertise">
                <div className="rounded-3xl bg-warm p-8 lg:p-12" style={{ borderLeft: "5px solid #E7343F" }}>
                    <span className="eyebrow-warm">Wer dahinter steht</span>
                    <h2 className="font-heading mt-3 text-3xl font-bold text-warm-strong">Über 20 Jahre Personal-, Arbeitsmarkt- und Integrationsexpertise</h2>
                    <p className="mt-4 text-warm leading-relaxed">
                        Bewerbung24.ai wird von einem Team rund um Gründerin E. Decker entwickelt — mit über 20 Jahren Praxis in Recruiting,
                        Karriereberatung, Outplacement und Fachkräfteintegration. Diese Erfahrung aus der Begleitung von Fach- und Führungskräften,
                        beruflicher Neuorientierung, Hochschullehre und Arbeitsmarktintegration fließt direkt in unsere KI-Vorlagen,
                        den Qualitäts-Check und die Stärkenanalyse ein.
                    </p>
                    <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-sm text-warm-strong">
                        {[
                            "Recruiting & Personalauswahl aus Arbeitgebersicht",
                            "Karriereberatung & Outplacement-Begleitung",
                            "Arbeitsmarktintegration & Fachkräftesicherung",
                            "Zusammenarbeit mit Schulen, Hochschulen, Jobcentern & Bildungsträgern",
                        ].map((item) => (
                            <li key={item} className="flex items-start gap-2.5">
                                <span className="h-6 w-6 rounded-full flex items-center justify-center text-white shrink-0 mt-0.5" style={{ background: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)" }}>
                                    <Check size={12} strokeWidth={3} />
                                </span>
                                {item}
                            </li>
                        ))}
                    </ul>
                    <div className="mt-7 flex flex-wrap gap-3">
                        <Link to="/partner" data-testid="about-partner-link" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm btn-primary-grad">
                            Für Partner & Institutionen <ArrowRight size={14} />
                        </Link>
                        <Link to="/kontakt" data-testid="about-contact-link" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-white border-2 border-zinc-200 hover:border-warm-red text-warm-strong transition-colors">
                            Kontakt aufnehmen
                        </Link>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}
